import React from 'react';
import HighlighterCustom from '@/components/HighlighterCustom';

/**
 * Helper function to create a render function with highlight for text values
 * The caller is responsible for extracting the correct value to highlight
 * @param {string} searchTerm - Search term to highlight
 * @param {Function} valueExtractor - Optional function to extract text from value (value, record, index) => string
 * @returns {Function} Render function with highlight applied
 *
 * @example
 * // Simple text value
 * render: createHighlightRender(searchTerm)
 *
 * // Extract from nested object
 * render: createHighlightRender(searchTerm, (value) => value?.name)
 *
 * // Complex extraction
 * render: createHighlightRender(searchTerm, (value, record) => `${value.first} ${value.last}`)
 */
export function createHighlightRender(searchTerm, valueExtractor = null) {
  return (value, record, index) => {
    // Extract text value using provided function or use value as is
    const textValue = valueExtractor ? valueExtractor(value, record, index) : value;

    // Handle null/undefined/empty values
    if (textValue == null || textValue === '') {
      return null;
    }

    // If it's already a React element, return as is
    if (React.isValidElement(textValue)) {
      return textValue;
    }

    return <HighlighterCustom textToHighlight={String(textValue)} searchTerm={searchTerm} />;
  };
}
